import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { getV1CompaniesId } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { COMPANY_SELECTOR_SELECTED_COMPANY_STORAGE_KEY } from '~constants/consts';
import { CompanyBasic } from '~hooks/useCompany/models';
import { CompanySelectorMenu } from '~layout/AppHeader/CompanySelector/CompanySelectorMenu';
import { useStores } from '~store';

const CompanySelector = ({ handleChange }: { handleChange?: () => void }) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const currentCompany = userStore.currentCompanies[0];

  const handleCompanyChange = (company: CompanyBasic) => {
    localStorage.setItem(COMPANY_SELECTOR_SELECTED_COMPANY_STORAGE_KEY, company.id);
    userStore.setCurrentCompanies([company]);

    if (handleChange) {
      handleChange();
    }
  };

  useEffect(() => {
    const storedCompanyId = localStorage.getItem(
      COMPANY_SELECTOR_SELECTED_COMPANY_STORAGE_KEY,
    );

    if (storedCompanyId && storedCompanyId !== currentCompany.id) {
      // trigger the company change immediately to prevent pages from loading data for the
      // default user company while "getV1CompaniesId" is in flight. once this is done, we
      // can fire a proper company fetch to grab its info.
      handleCompanyChange({ id: storedCompanyId, legalName: '' } as CompanyBasic);

      getV1CompaniesId({ path: { id: storedCompanyId } })
        .then((response) => {
          const company = CompanyBasic.parse(response.data.data);

          handleCompanyChange(company);
        })
        .catch((error) => {
          const isDeletedCompanyLookup =
            isAxiosError(error) && error.response?.status === 404;

          if (isDeletedCompanyLookup) {
            // A late 404 for the dead company must not clobber a newer selection
            // the user made while this lookup was in flight — only reset if the
            // stored id is still the one we looked up.
            const storedCompanyStillSelected =
              localStorage.getItem(COMPANY_SELECTOR_SELECTED_COMPANY_STORAGE_KEY) ===
              storedCompanyId;

            if (storedCompanyStillSelected) {
              // Fall back to the default company through the same path a manual
              // switch uses, so the optional handleChange callback (dispatch /
              // live map / driver scheduler resets) also runs. Clearing storage
              // directly would skip those resets and leave stale company-scoped
              // state behind.
              const defaultCompany = userStore.userCompanies[0];
              if (defaultCompany) {
                handleCompanyChange(defaultCompany);
              } else {
                localStorage.removeItem(COMPANY_SELECTOR_SELECTED_COMPANY_STORAGE_KEY);
              }
            }

            return;
          }

          throw error;
        });
    }
  }, [currentCompany]);

  return (
    <Box
      sx={{
        borderRight: `1px solid ${theme.palette.divider}`,
        transition: 'all .3s ease',
        width: '240px',
        height: '100%',
        '&:hover': { bgcolor: theme.palette.action.hover },
      }}
    >
      <CompanySelectorMenu
        multiple={false}
        currentCompany={currentCompany}
        onChange={handleCompanyChange}
      />
    </Box>
  );
};

export default observer(CompanySelector);
